import { useEffect, useState } from "react";

const STORE_NAMES = {
  1: "Estofados",
  2: "Colchões",
  3: "Mezzo PG",
  4: "Mezzo Curitiba",
};

const STATUS_LABELS = {
  pending: "Aguardando tentativa",
  processing: "Em processamento",
  exhausted: "Revisão necessária",
};

const formatDateTime = value => {
  if (!value) return "—";
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime())
    ? "—"
    : parsed.toLocaleString("pt-BR", { timeZone:"America/Sao_Paulo" });
};

async function getQueue(storeId) {
  const query = storeId ? `?storeId=${encodeURIComponent(storeId)}` : "";
  const response = await fetch(`/api/lift/pendencias-cartoes${query}`, {
    credentials:"same-origin",
    cache:"no-store",
  });
  const body = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(body.error || `Não foi possível carregar (HTTP ${response.status})`);
  }
  return body;
}

export default function LiftCardPendingQueue({ currentUser }) {
  const role = currentUser?.role;
  const isManager = role === "manager";
  const isAdministrator = role === "admin" || role === "superadmin";
  const canView = isManager || isAdministrator;
  const [selectedStore, setSelectedStore] = useState("");
  const [items, setItems] = useState([]);
  const [maxAttempts, setMaxAttempts] = useState(3);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [refreshKey, setRefreshKey] = useState(0);
  const managerStoreId = currentUser?.storeId;
  const requestStoreId = isManager ? managerStoreId : selectedStore;

  useEffect(() => {
    if (!canView) {
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    setError("");
    getQueue(requestStoreId)
      .then(body => {
        if (cancelled) return;
        setItems(Array.isArray(body.items) ? body.items : []);
        setMaxAttempts(Number(body.maxAttempts) || 3);
        setLoading(false);
      })
      .catch(err => {
        if (cancelled) return;
        setItems([]);
        setError(err.message || "Não foi possível carregar a fila de cartões.");
        setLoading(false);
      });
    return () => { cancelled = true; };
  }, [canView, requestStoreId, refreshKey]);

  if (!canView) return null;

  const panelStyle = {
    border:"1px solid #D8D2C5",
    borderRadius:12,
    background:"#F8F6F0",
    padding:16,
    color:"#293B3C",
  };
  const buttonStyle = {
    border:"1px solid #B8C4BD",
    background:"#FFFDF8",
    color:"#254344",
    borderRadius:8,
    padding:"8px 12px",
    fontWeight:700,
    cursor:"pointer",
  };
  const storeLabel = storeId => STORE_NAMES[Number(storeId)] || `Loja ${storeId}`;

  return <section aria-labelledby="lift-card-queue-title" style={{ ...panelStyle, marginTop:20 }}>
    <div style={{ display:"flex", alignItems:"flex-start", justifyContent:"space-between", gap:12, flexWrap:"wrap" }}>
      <div>
        <h3 id="lift-card-queue-title" style={{ margin:"0 0 5px", fontSize:16 }}>Fila de cartões pendentes</h3>
        <p style={{ margin:0, fontSize:13, lineHeight:1.5, color:"#526563" }}>
          A atualização financeira da venda não espera o envio do cartão. Aguardando revisão manual não significa que o cartão foi concluído.
        </p>
      </div>
      <button type="button" style={buttonStyle} onClick={() => setRefreshKey(value => value + 1)} disabled={loading}>
        {loading ? "Atualizando…" : "Atualizar"}
      </button>
    </div>

    {isAdministrator && <label style={{ display:"inline-flex", alignItems:"center", gap:8, marginTop:12, fontSize:13, fontWeight:700 }}>
      Loja
      <select
        aria-label="Filtrar fila por loja"
        value={selectedStore}
        onChange={event => setSelectedStore(event.target.value)}
        style={{ padding:"7px 9px", border:"1px solid #B8C4BD", borderRadius:7, background:"#FFFDF8", color:"#293B3C" }}
      >
        <option value="">Todas as lojas permitidas</option>
        {Object.entries(STORE_NAMES).map(([id, name]) =>
          <option key={id} value={id}>{name}</option>
        )}
      </select>
    </label>}

    {loading && <div role="status" aria-label="Carregando fila de cartões" style={{ display:"grid", gap:8, marginTop:14 }}>
      {[0, 1].map(row => <div key={row} style={{ height:58, borderRadius:8, background:"#EAE7DE" }} />)}
    </div>}
    {!loading && error && <div role="alert" style={{ marginTop:14, padding:12, borderRadius:8, background:"#F9E8E3", color:"#853D31" }}>
      <div>{error}</div>
      <button type="button" style={{ ...buttonStyle, marginTop:8 }} onClick={() => setRefreshKey(value => value + 1)}>
        Atualizar fila
      </button>
    </div>}
    {!loading && !error && !items.length && <div style={{ marginTop:14, padding:14, border:"1px dashed #B8C4BD", borderRadius:8, color:"#526563" }}>
      Nenhum cartão pendente nesta consulta.
    </div>}
    {!loading && !error && items.length > 0 && <div style={{ display:"grid", gap:8, marginTop:14 }}>
      {items.map((item, index) => {
        const attempts = Number(item.attempts) || 0;
        const limit = Number(item.maxAttempts) || maxAttempts;
        const exhausted = item.status === "exhausted" || attempts >= limit;
        return <article key={`${item.storeId}_${item.pedidoId}_${index}`} style={{
          padding:12,
          border:"1px solid #D8D2C5",
          borderLeft:`4px solid ${exhausted ? "#A34F3F" : "#BE8B42"}`,
          borderRadius:8,
          background:"#FFFDF8",
        }}>
          <div style={{ display:"flex", justifyContent:"space-between", gap:10, flexWrap:"wrap" }}>
            <strong>Pedido #{item.pedidoId} · {storeLabel(item.storeId)}</strong>
            <strong style={{ color:exhausted ? "#853D31" : "#785A29" }}>
              {STATUS_LABELS[item.status] || "Status não informado"}
            </strong>
          </div>
          <div style={{ marginTop:6, fontSize:13, lineHeight:1.6 }}>
            Tentativas: {attempts}/{limit}{exhausted ? attempts >= limit ? " · Limite atingido" : " · Retentativas suspensas" : ""}
            <br />Motivo mais recente: {item.lastReason || "Ainda não há motivo informado."}
            <br />Criado: {formatDateTime(item.createdAt)} · Atualizado: {formatDateTime(item.updatedAt)}
            {!exhausted && item.nextAttemptAt && <><br />Próxima tentativa: {formatDateTime(item.nextAttemptAt)}</>}
          </div>
        </article>;
      })}
    </div>}
  </section>;
}
